import { useApiQuery } from "../../services/api";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useActiveLang } from "./hooks/useActiveLang";
import { getItemTranslation } from "./utils/getItemTranslation";
import { searchLabels } from "./labelSearch";

/**
 * The catalogue rows a model recognizes behind what a practitioner typed, on top
 * of what `searchLabels` already found locally.
 *
 * This is the layer that answers "MPR" with "Médecine physique et de
 * réadaptation", "anapath" with "Anatomie et cytologie pathologiques", and "je
 * pose des prothèses de hanche" with the practices that cover it. The local
 * layer cannot: an abbreviation is far from its label by every string measure.
 */

/** Long enough that the practitioner has stopped typing a word, short enough
 * that they have not yet decided the screen is broken. */
const DEBOUNCE_MS = 400;

/**
 * When the call is worth making.
 *
 * Not on every keystroke past three characters, which is what the plan started
 * from: the local layer answers 42 queries out of 74 on its own, and the model
 * adds nothing to those. Firing only on an EMPTY local result would be wrong in
 * the other direction — "ENT" and "réa" are answered locally, confidently, and
 * beside the point — so a short query goes out too. Measured: 42 % of the calls
 * saved, and not one case lost.
 */
const TRIGGER = { minLength: 3, maxLength: 80, alwaysUpToLength: 4 } as const;

export type LabelSearchAssistCatalogue =
  /** The uids ride along because the screen, not the API, decides which
   * specialties exist for this reader: the hidden list and the chosen
   * profession both cut the parent tags down, sometimes to two rows of 95. A
   * model reading the full list would answer with rows this screen then drops. */
  | { readonly kind: "specialty"; readonly uids: readonly string[] }
  | { readonly kind: "practice"; readonly specialtyUid: string };

/** A catalogue with nothing in it yet — the specialty list has not loaded, or
 * the profession narrowed it to nothing. There is nothing to search, and the
 * route refuses an empty list rather than guess at the full one. */
function hasRowsToSearch(catalogue: LabelSearchAssistCatalogue): boolean {
  switch (catalogue.kind) {
    case "specialty":
      return catalogue.uids.length > 0;
    case "practice":
      return true;
    default:
      throw new Error(catalogue satisfies never);
  }
}

export function useLabelSearchAssist(args: {
  /** Null where there is no catalogue to search yet — a settings screen whose
   * practitioner has declared no Specialty. Nothing goes out, and nothing comes
   * back. */
  readonly catalogue: LabelSearchAssistCatalogue | null;
  readonly query: string;
  /** How many rows the local layer found for the CURRENT keystroke. */
  readonly localResultCount: number;
}): {
  readonly assistedUids: readonly string[];
  readonly isAssisting: boolean;
  /** The query BOTH layers have now ANSWERED, or null while one of them still
   * might, and null when the model layer failed instead of answering. The only
   * moment at which "this catalogue does not hold it" is a statement rather
   * than a guess — which is what the miss metric reports. */
  readonly settledQuery: string | null;
} {
  const { activeLang } = useActiveLang();
  const trimmed = args.query.trim();
  const debounced = useDebouncedValue(trimmed, DEBOUNCE_MS);

  // The catalogue rides ALONG with the query rather than beside it, so that
  // "there is a call to make" and "here is what to call it with" cannot drift
  // apart and force a second null check the type system would not verify.
  const active =
    args.catalogue !== null &&
    hasRowsToSearch(args.catalogue) &&
    debounced.length >= TRIGGER.minLength &&
    // The route refuses a longer query, and a refusal is indistinguishable from
    // an outage on this side. The bound belongs where the request is built.
    debounced.length <= TRIGGER.maxLength &&
    debounced === trimmed &&
    (args.localResultCount === 0 ||
      debounced.length <= TRIGGER.alwaysUpToLength)
      ? { query: debounced, catalogue: args.catalogue }
      : null;

  const assisted = useApiQuery(
    "/webapp/public/search-assist/labels",
    active === null
      ? false
      : {
          query:
            active.catalogue.kind === "specialty"
              ? {
                  kind: "specialty",
                  query: active.query,
                  language: activeLang,
                  uids: [...active.catalogue.uids],
                }
              : {
                  kind: "practice",
                  query: active.query,
                  language: activeLang,
                  specialtyUid: active.catalogue.specialtyUid,
                },
        }
  );

  return {
    assistedUids: assisted.state === "success" ? assisted.body.uids : [],
    // A success that ANSWERED. Two different failures would otherwise be filed
    // as a hole in the catalogue, which is the exact pollution this metric was
    // rewritten to remove: a 429 behind a hospital NAT, which does not reach
    // `success` at all, and a model timeout, which does — the route answers 200
    // with an empty list on purpose, so that a signup step never shows an error
    // over an answer it can do without, and says so in `answered`.
    settledQuery:
      active !== null && assisted.state === "success" && assisted.body.answered
        ? active.query
        : null,
    // What the screen shows a waiting state for. It covers the debounce too:
    // between the keystroke and the request there is nothing on screen and no
    // request in flight, and "no result" would be a lie for another 400 ms.
    isAssisting:
      trimmed.length >= TRIGGER.minLength &&
      args.localResultCount === 0 &&
      (debounced !== trimmed || assisted.state === "pending"),
  };
}

/**
 * The rows of a catalogue that answer a query: what the string layer found,
 * instantly, then what the model recognized, appended below.
 *
 * Appended, never re-ranked. A row already on screen does not move because a
 * network answer arrived — the practitioner's thumb is already travelling
 * towards it, and a list that reshuffles under a thumb is worse than a list that
 * is one row short.
 */
export function useAssistedLabelSearch<T extends { uid: string }>(args: {
  readonly items: readonly T[];
  readonly query: string;
  readonly labelOf: (item: T) => string;
  readonly catalogue: LabelSearchAssistCatalogue;
}): {
  readonly rows: readonly T[];
  readonly isAssisting: boolean;
  readonly settledQuery: string | null;
} {
  const local = searchLabels({
    items: args.items,
    query: args.query,
    labelOf: args.labelOf,
  });
  const { assistedUids, isAssisting, settledQuery } = useLabelSearchAssist({
    catalogue: args.catalogue,
    query: args.query,
    localResultCount: local.length,
  });

  const shown = new Set(local.map((item) => item.uid));
  const byUid = new Map(args.items.map((item) => [item.uid, item]));
  const added = assistedUids.flatMap((uid) => {
    if (shown.has(uid)) return [];
    const item = byUid.get(uid);
    return item === undefined ? [] : [item];
  });
  return { rows: [...local, ...added], isAssisting, settledQuery };
}

/**
 * The specialty list as the signup carousel and the settings sheet both read it:
 * alphabetical in the language the reader is actually reading, then searched.
 *
 * The endpoint orders by `uid`, which is alphabetical among ENGLISH slugs and
 * therefore nothing at all in the other seven: a French list opens on "Soins
 * aigus, Addictologie, Gestion de la santé, Médecine aérospatiale". One SQL
 * order cannot be alphabetical in eight languages, so the sort belongs here —
 * where the rest of the app already does it (SpecialtyFilterPanel,
 * EditProfileView, TopicsInterestsEditor).
 *
 * Sorted BEFORE the search, because the search returns its matches in input
 * order and appends what it recovered from a typo or from the model; sorting
 * after would shuffle that tail back into the list.
 */
export function useSpecialtySearch<
  T extends { uid: string; translations: Record<string, string | null> },
>(args: {
  readonly specialties: readonly T[];
  readonly query: string;
}): {
  readonly rows: readonly T[];
  readonly isAssisting: boolean;
  readonly settledQuery: string | null;
} {
  const sorted = [...args.specialties].sort((a, b) =>
    getItemTranslation(a).localeCompare(getItemTranslation(b))
  );
  return useAssistedLabelSearch({
    items: sorted,
    query: args.query,
    labelOf: (specialty) => getItemTranslation(specialty),
    catalogue: {
      kind: "specialty",
      uids: sorted.map((specialty) => specialty.uid),
    },
  });
}
